import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Card, Table, Button, Form, Modal, Row, Col } from 'react-bootstrap';
import { Trash2, Plus, Calendar } from 'lucide-react';
import { useAlert } from '../context/AlertContext';

interface Holiday {
    id: number;
    name: string;
    date: string;
}

const HolidayManager = () => {
    const { showAlert } = useAlert();
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newHoliday, setNewHoliday] = useState({ name: '', date: '' });

    useEffect(() => {
        fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
        try {
            setLoading(true);
            const response = await api.get('/holidays/');
            setHolidays(response.data);
        } catch (error) {
            console.error('Error fetching holidays:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddHoliday = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/holidays/', newHoliday);
            setShowAddModal(false);
            setNewHoliday({ name: '', date: '' });
            fetchHolidays();
        } catch (error) {
            console.error('Error adding holiday:', error);
            showAlert('Erro ao adicionar feriado.', { type: 'error' });
        }
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja remover este feriado?', {
            type: 'warning',
            title: 'Remover Feriado',
            confirmable: true,
            confirmText: 'Remover',
            cancelText: 'Cancelar',
            confirmVariant: 'danger',
            onConfirm: async () => {
                try {
                    await api.delete(`/holidays/${id}/`);
                    fetchHolidays();
                } catch (error) {
                    console.error('Error deleting holiday:', error);
                }
            }
        });
    };

    return (
        <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0 d-flex align-items-center gap-2">
                    <Calendar size={24} /> Feriados
                </h2>
                <Button variant="success" onClick={() => setShowAddModal(true)} className="d-flex align-items-center gap-2">
                    <Plus size={18} /> Novo Feriado
                </Button>
            </div>

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <div className="custom-table-container">
                        <Table hover className="custom-table mb-0">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Nome</th>
                                    <th style={{ width: '100px' }} className="text-end">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {holidays.map(holiday => (
                                    <tr key={holiday.id}>
                                        <td>{new Date(holiday.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</td>
                                        <td>{holiday.name}</td>
                                        <td className="text-end">
                                            <Button
                                                variant="link"
                                                className="text-secondary p-1"
                                                onClick={() => handleDelete(holiday.id)}
                                            >
                                                <Trash2 size={18} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {holidays.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan={3} className="text-center py-4 text-secondary">
                                            Nenhum feriado cadastrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Adicionar Feriado</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddHoliday}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nome do Feriado</Form.Label>
                            <Form.Control
                                value={newHoliday.name}
                                onChange={e => setNewHoliday({ ...newHoliday, name: e.target.value })}
                                required
                                autoFocus
                                placeholder="Ex: Natal"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Data</Form.Label>
                            <Form.Control
                                type="date"
                                value={newHoliday.date}
                                onChange={e => setNewHoliday({ ...newHoliday, date: e.target.value })}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            Salvar
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default HolidayManager;
